/**
 * Responsive prop class resolver
 *
 * Converts ResponsiveProp<T> values into arrays of BEM class names
 * with breakpoint prefixes for responsive behavior.
 */

import type { Breakpoint, ResponsiveProp } from "../types";

import { BREAKPOINT_ORDER } from "../constants";

interface ResolveParams<T extends string> {
  baseClass: string;
  propValue: ResponsiveProp<T>;
}

/**
 * Resolves a responsive prop to an array of BEM class names.
 *
 * Mobile-first approach: the smallest breakpoint in the object becomes
 * the base class (no prefix), and larger breakpoints get prefixed.
 *
 * @example
 * // Static value
 * resolveResponsiveClasses({
 *   baseClass: 'ui-button',
 *   propValue: 'md'
 * })
 * // → ["ui-button--md"]
 *
 * @example
 * // Responsive object
 * resolveResponsiveClasses({
 *   baseClass: 'ui-button',
 *   propValue: { sm: 'lg', lg: 'sm' }
 * })
 * // → ["ui-button--lg", "lg:ui-button--sm"]
 * // Mobile gets large size, desktop (lg+) gets small size
 */
export function resolveResponsiveClasses<T extends string>({
  baseClass,
  propValue,
}: ResolveParams<T>): string[] {
  // Static value: single class
  if (typeof propValue === "string") {
    return [`${baseClass}--${propValue}`];
  }

  // Responsive object: sort by breakpoint order and generate classes
  const entries = Object.entries(propValue) as [Breakpoint, T][];
  entries.sort((a, b) => BREAKPOINT_ORDER.indexOf(a[0]) - BREAKPOINT_ORDER.indexOf(b[0]));

  return entries.map(([breakpoint, value], index) => {
    // First entry (smallest breakpoint) = mobile-first base
    if (index === 0) {
      return `${baseClass}--${value}`;
    }

    // Subsequent entries get breakpoint prefix
    return `${breakpoint}:${baseClass}--${value}`;
  });
}
